import { PermissionGate } from "@components/PermissionGate";
import { useAuth } from "@contexts/auth";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { ActionIcon, Button, Group, ScrollArea, Text } from "@mantine/core";
import { useDisclosure, useNetwork } from "@mantine/hooks";
import { IconArrowLeft, IconMessage } from "@tabler/icons-react";

import { NetworkNotification } from "@components/NetworkNotification";
import { PatientNotesModal } from "@components/PatientNotesModal";

import { getPatientNavLinks } from "@data/patientNavLinks";

import {
  StyledWithPatientNavbar,
  StyledWrapper,
} from "./WithPatientNavbar.styled";


interface WithPatientNavbarProps {
  children: React.ReactNode;
}

export const WithPatientNavbar = ({ children }: WithPatientNavbarProps) => {
  const { t } = useTranslation();
  const { canAccessPath } = useAuth();

  const { patientId } = useParams();
  const navigate = useNavigate();
  const { online } = useNetwork();
  const [opened, { open, close }] = useDisclosure(false);

  return (
    <>
      <NetworkNotification />
      <StyledWithPatientNavbar
        className={`${!online ? "show-notification" : "hide-notification"}`}>
        <div className="upper-nav">
          <Button
            variant="subtle"
            size="compact-sm"
            color="accent.4"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/patients")}>
            {t("back-to-patients")}
          </Button>

          <Group>
            <PermissionGate permission="notes.view"><ActionIcon
              size={40}
              radius={"xl"}
              variant="outline"
              onClick={open}>
              <IconMessage size={20} />
            </ActionIcon></PermissionGate>
            <Button
              variant="outline"
              color="accent.4"
              size="sm"
              onClick={() => navigate(`/patients/${patientId}/profile`)}>
              {t("patient-id")}:&nbsp;
              <Text fw={600} fz={"inherit"} ff={"var(--font-secondary)"}>
                {patientId}
              </Text>
            </Button>
          </Group>
        </div>

        <ScrollArea>
          <ul className="nav-links">
            {getPatientNavLinks().filter(item => canAccessPath(`/patients/patient/${item.value}`)).map(({ id, label, value }) => (
              <li className="nav-links-item" key={id}>
                <NavLink to={`/patients/${patientId}/${value}`}>
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </ScrollArea>

        <PermissionGate permission="notes.view"><PatientNotesModal opened={opened} close={close} /></PermissionGate>
      </StyledWithPatientNavbar>
      <StyledWrapper
        className={`${!online ? "show-notification" : "hide-notification"}`}>
        {children}
      </StyledWrapper>
    </>
  );
};
